/**
 * useContactLabelsCount
 *
 * Retorna a etiqueta (etapa do funil) atualmente atribuida a um contato + o
 * nome do funil correspondente. Usado pelo header dos ChatWindows (wa-personal
 * e wa-business) para:
 *   - decidir o texto do menu "Definir funil/etapa" vs "Alterar funil/etapa"
 *     (via hasLabel);
 *   - exibir DIRETO no cartao do contato o chip "Funil · Etapa" (label +
 *     funnelName).
 *
 * Regra do dominio: 1 etiqueta por contato — entao a "atual" = labels[0].
 * Multi-funil (flag `multi_funnel`, 2026-09-16): o contato pode ter uma
 * etiqueta POR FUNIL; `entries` traz todas ({label, funnelName}, uma por
 * funil) e `label`/`funnelName` continuam sendo a primeira (contrato antigo).
 *
 * Recarrega quando muda o telefone do contato (troca de chat) ou quando o
 * caller chama `reload()` — tipicamente apos fechar o LabelPickerModal, para
 * o cartao/menu refletirem o estado mais novo.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import authService from '../services/global/authService';
import { useSSEEvent, SSE_EVENTS } from '../contexts/SSEContext';

// Catalogo de funis: o cache privado (TTL 2min) foi promovido ao requestCache
// DENTRO de authService.listFunnels (TTL 5min, compartilhado com todos os
// call sites e invalidado nas mutacoes). Aqui fica so o passthrough com o
// fallback de erro e o `force` da auto-cura.
let _lastFunnels = [];
function loadFunnels(force = false) {
  return authService.listFunnels({ force })
    .then((list) => {
      _lastFunnels = Array.isArray(list) ? list : [];
      return _lastFunnels;
    })
    .catch(() => _lastFunnels);
}

const _digits = (v) => String(v || '').replace(/\D/g, '');
// Telefones podem divergir no nono digito BR / prefixo 55 — compara exato
// primeiro e cai para o sufixo de 8 digitos (falso positivo = 1 reload barato).
function _samePhone(a, b) {
  const da = _digits(a);
  const db = _digits(b);
  if (!da || !db) return false;
  if (da === db) return true;
  return da.length >= 8 && db.length >= 8 && da.slice(-8) === db.slice(-8);
}

export default function useContactLabelsCount(contactPhone) {
  const [count, setCount] = useState(0);
  const [label, setLabel] = useState(null);       // etiqueta/etapa atual
  const [funnelName, setFunnelName] = useState(null);
  const [entries, setEntries] = useState([]);     // [{label, funnelName}] uma por funil
  const reloadKeyRef = useRef(0);
  const [reloadKey, setReloadKey] = useState(0);
  const prevPhoneRef = useRef(null);

  const reload = useCallback(() => {
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, []);

  // Chip ao vivo: mudanca de etiqueta DESTE contato (modal, Kanban, regra de
  // funil, agente — via SSE do backend ou eco local do LabelPickerModal)
  // recarrega na hora. Antes o chip so atualizava ao fechar o modal ou
  // trocar de chat.
  const phoneRef = useRef(contactPhone);
  phoneRef.current = contactPhone;
  useSSEEvent(SSE_EVENTS.CONTACT_LABELS_CHANGED, useCallback((data) => {
    if (!phoneRef.current) return;
    // Instagram: o evento vem com contact_ref ('instagram:{igsid}') e phone
    // vazio (contato de IG nao tem telefone). Casa pelo ref exato; WhatsApp
    // segue casando por telefone (com tolerancia do 9o digito).
    if (data?.contact_ref && String(data.contact_ref) === String(phoneRef.current)) {
      // Frente C4: a leitura passa pelo requestCache; derruba antes de reler.
      authService.invalidateContactLabels(phoneRef.current);
      reload();
      return;
    }
    if (data?.phone && _samePhone(data.phone, phoneRef.current)) {
      authService.invalidateContactLabels(phoneRef.current);
      reload();
    }
  }, [reload]));

  useEffect(() => {
    // Troca de contato: LIMPA o chip do anterior de imediato (senão o
    // "Funil · Etapa" de A ficaria visível no header de B até o fetch de B
    // voltar). Num reload (reloadKey) do MESMO contato não limpa, p/ não piscar.
    if (contactPhone !== prevPhoneRef.current) {
      setCount(0); setLabel(null); setFunnelName(null); setEntries([]);
      prevPhoneRef.current = contactPhone;
    }
    if (!contactPhone) return undefined;
    let cancelled = false;
    Promise.all([authService.getContactLabels(contactPhone), loadFunnels()])
      .then(async ([res, funnels]) => {
        if (cancelled) return;
        const labels = res?.data || [];
        const arr = Array.isArray(labels) ? labels : [];
        const primary = arr[0] || null;
        let f = primary
          ? (funnels.find((x) => x.id === primary.pipeline_id) || null)
          : null;
        // Auto-cura: funil da etapa ausente do cache (criado depois) → refetch
        // forçado antes de esconder o chip.
        let list = funnels;
        if (primary && !f) {
          const fresh = await loadFunnels(true);
          if (cancelled) return;
          list = fresh;
          f = fresh.find((x) => x.id === primary.pipeline_id) || null;
        }
        setCount(arr.length);
        setLabel(primary);
        setFunnelName(f ? f.name : null);
        // Uma entrada por funil (a primeira etiqueta de cada funil vence).
        const seen = new Set();
        const all = [];
        for (const l of arr) {
          const fid = l.pipeline_id || '';
          if (seen.has(fid)) continue;
          seen.add(fid);
          const fn = list.find((x) => x.id === l.pipeline_id);
          all.push({ label: l, funnelName: fn ? fn.name : null });
        }
        setEntries(all);
      })
      .catch(() => {
        // Falha silenciosa — caimos no default "Definir funil/etapa", que e
        // o pior caso aceitavel (texto generico, nao incorreto).
        if (!cancelled) { setCount(0); setLabel(null); setFunnelName(null); setEntries([]); }
      });
    return () => { cancelled = true; };
  }, [contactPhone, reloadKey]);

  return {
    count,
    hasLabels: count > 0,
    hasLabel: count > 0,
    reload,
    // Dados da etapa/funil atual para o chip do cartao.
    label,
    funnelName,
    stageName: label ? label.name : null,
    color: label ? label.color : null,
    // Multi-funil: todas as etapas do contato, uma por funil.
    entries,
  };
}
